import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PenyuluhPusatDataType } from './types'
import type {
  FilterPenyuluhPusatValues,
  MutationPenyuluhPusatValues
} from './validations'

const PROVINSI_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['penyuluh-pusat', key] as const

const getAll = (
  params?: FilterPenyuluhPusatValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PenyuluhPusatDataType>>
> => ({
  queryKey: PROVINSI_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PenyuluhPusatDataType>>
      >('/kelembagaan/pusat', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PenyuluhPusatDataType>> => ({
  queryKey: PROVINSI_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<PenyuluhPusatDataType>
      >(`/kelembagaan/pusat/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhPusatDataType>,
  MutationPenyuluhPusatValues
> => ({
  mutationFn: async (data: MutationPenyuluhPusatValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PenyuluhPusatDataType>
      >('/kelembagaan/pusat', {
        ...data,
        id_poktan: undefined,
        id_desa: data.id_desa.value,
        id_kecamatan: data.district_id.value,
        latitude: data.latitude?.toString(),
        longitude: data.longitude?.toString()
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhPusatDataType>,
  MutationPenyuluhPusatValues
> => ({
  mutationFn: async (data: MutationPenyuluhPusatValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PenyuluhPusatDataType>
      >(`/kelembagaan/pusat/${data.id_penyuluh_pusat}`, {
        ...data,
        id_poktan: undefined,
        id_desa: data.id_desa.value,
        id_kecamatan: data.district_id.value,
        latitude: data.latitude?.toString(),
        longitude: data.longitude?.toString()
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhPusatDataType>,
  { id_penyuluh_pusat: string }
> => ({
  mutationFn: async (data: { id_penyuluh_pusat: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PenyuluhPusatDataType>
      >(`/kelembagaan/pusat/${data.id_penyuluh_pusat}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (params?: FilterPenyuluhPusatValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/pusat/export`, { params })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const penyuluhPusat = {
  queryKey: [PROVINSI_QUERY_KEY(undefined)[0]],
  getAll,
  create,
  getById,
  update,
  deleteData,
  exportData
}
